import { useState, useEffect, useCallback } from 'react';
import API from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Plus, Upload, Trash2, FileSignature, Settings2, Download, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import PdfFieldEditor from '@/components/PdfFieldEditor';

const inputClass = "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#0073ea]/50 focus:ring-[#0073ea]";

function EditableTemplateCard({ tmpl, onUpdate, onDelete, onDownload, onEditFields }) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(tmpl.name);
  const [editDesc, setEditDesc] = useState(tmpl.description || '');
  const [editPattern, setEditPattern] = useState(tmpl.file_name_pattern || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!editName.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await API.put(`/templates/${tmpl.id}`, {
        name: editName.trim(),
        description: editDesc.trim(),
        file_name_pattern: editPattern.trim(),
      });
      toast.success('Template updated');
      setEditing(false);
      onUpdate();
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  const handleCancel = () => {
    setEditName(tmpl.name);
    setEditDesc(tmpl.description || '');
    setEditPattern(tmpl.file_name_pattern || '');
    setEditing(false);
  };

  return (
    <Card data-testid={`template-card-${tmpl.id}`} className="bg-white border-gray-200 hover:border-[#0073ea]/30 hover:shadow-sm transition-all duration-300">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="h-10 w-10 rounded-lg bg-purple-50 flex items-center justify-center">
            <FileSignature className="h-5 w-5 text-[#a25ddc]" strokeWidth={1.5} />
          </div>
          <div className="flex gap-1">
            {!editing && (
              <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-[#0073ea]" onClick={() => setEditing(true)} data-testid={`edit-template-${tmpl.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            )}
            <Button variant="ghost" size="icon" className="h-7 w-7 text-gray-400 hover:text-gray-900" onClick={() => onDownload(tmpl.id)} data-testid={`download-template-${tmpl.id}`}>
              <Download className="h-3.5 w-3.5" />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => onDelete(tmpl.id)} data-testid={`delete-template-${tmpl.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {editing ? (
          <div className="space-y-2.5">
            <Input data-testid={`edit-name-${tmpl.id}`} value={editName} onChange={e => setEditName(e.target.value)} placeholder="Template Name" className={inputClass + " text-sm"} />
            <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description" className={inputClass + " text-xs"} />
            <div>
              <Input data-testid={`edit-pattern-${tmpl.id}`} value={editPattern} onChange={e => setEditPattern(e.target.value)} placeholder="File Name Pattern" className={inputClass + " text-xs"} />
              <div className="flex gap-1 mt-1">
                {['{property_address}', '{seller_name}', '{folio}'].map(tag => (
                  <button key={tag} type="button" onClick={() => setEditPattern(prev => prev ? `${prev} ${tag}` : tag)}
                    className="px-1.5 py-0.5 text-[9px] rounded bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100">
                    + {tag}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-1.5 pt-1">
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#0073ea] hover:bg-[#0060c2] text-white text-xs flex-1">
                <Check className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancel} className="text-gray-500 text-xs">
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h3 className="text-base font-semibold text-gray-900 mb-1">{tmpl.name}</h3>
            {tmpl.description && <p className="text-xs text-gray-500 mb-1">{tmpl.description}</p>}
            {tmpl.file_name_pattern && (
              <p className="text-[10px] text-purple-600 bg-purple-50 rounded px-1.5 py-0.5 inline-block mb-3" data-testid={`pattern-display-${tmpl.id}`}>
                File: {tmpl.file_name_pattern}
              </p>
            )}
            {!tmpl.file_name_pattern && tmpl.description && <div className="mb-3" />}
            <div className="flex items-center justify-between">
              <Badge variant="outline" className="text-xs border-gray-300 text-gray-500">
                {tmpl.fields?.length || 0} fields
              </Badge>
              <Button variant="ghost" size="sm" onClick={() => onEditFields(tmpl.id)} data-testid={`edit-fields-${tmpl.id}`} className="text-[#0073ea] hover:text-[#0060c2] text-xs">
                <Settings2 className="h-3.5 w-3.5 mr-1" /> Open & Assign Fields
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function ContractTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [fileNamePattern, setFileNamePattern] = useState('');
  const [file, setFile] = useState(null);
  const [editorTemplateId, setEditorTemplateId] = useState(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await API.get('/templates');
      setTemplates(res.data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Select a PDF file'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('file_name_pattern', fileNamePattern);
      formData.append('file', file);
      await API.post('/templates', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Template uploaded');
      setUploadOpen(false);
      setName(''); setDescription(''); setFileNamePattern(''); setFile(null);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/templates/${id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch { toast.error('Failed to delete'); }
  };

  const handleDownload = async (templateId) => {
    try {
      const res = await API.get(`/templates/${templateId}/download`, { responseType: 'blob' });
      const tmpl = templates.find(t => t.id === templateId);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tmpl?.name || 'template'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  // If PDF field editor is open, render it full-screen
  if (editorTemplateId) {
    return (
      <PdfFieldEditor
        templateId={editorTemplateId}
        onClose={() => setEditorTemplateId(null)}
        onSaved={() => { setEditorTemplateId(null); fetchTemplates(); }}
      />
    );
  }

  return (
    <div data-testid="contract-templates-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contract Templates</h1>
          <p className="text-gray-500 mt-1">Upload PDF templates and assign field positions</p>
        </div>
        <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
          <DialogTrigger asChild>
            <Button data-testid="upload-template-btn" className="bg-[#0073ea] hover:bg-[#0060c2] text-white">
              <Upload className="h-4 w-4 mr-2" /> Upload Template
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-gray-200 text-gray-900">
            <DialogHeader>
              <DialogTitle className="text-xl text-gray-900">Upload Contract Template</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpload} className="space-y-4 mt-2">
              <div className="space-y-2">
                <Label className="text-gray-700">Template Name *</Label>
                <Input data-testid="template-name-input" value={name} onChange={e => setName(e.target.value)} required placeholder="e.g. Standard Purchase Agreement" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Description</Label>
                <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description" className={inputClass} />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">File Name Pattern</Label>
                <div className="flex gap-2">
                  <Input
                    data-testid="file-name-pattern-input"
                    value={fileNamePattern}
                    onChange={e => setFileNamePattern(e.target.value)}
                    placeholder="e.g. {property_address} Addendum"
                    className={inputClass + " flex-1"}
                  />
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {['{property_address}', '{seller_name}', '{folio}'].map(tag => (
                    <button
                      key={tag}
                      type="button"
                      data-testid={`tag-${tag.replace(/[{}]/g, '')}`}
                      onClick={() => setFileNamePattern(prev => prev ? `${prev} ${tag}` : tag)}
                      className="px-2 py-0.5 text-[11px] font-medium rounded-md bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors"
                    >
                      + {tag}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400">Generated files will use this name. Tags get replaced with lead data.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">PDF File *</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#0073ea]/50 transition-colors bg-[#f6f7fb]">
                  <input
                    type="file"
                    accept=".pdf"
                    data-testid="template-file-input"
                    onChange={e => setFile(e.target.files[0])}
                    className="hidden"
                    id="pdf-upload"
                  />
                  <label htmlFor="pdf-upload" className="cursor-pointer">
                    <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">{file ? file.name : 'Click to select PDF'}</p>
                  </label>
                </div>
              </div>
              <Button type="submit" data-testid="upload-submit-btn" disabled={uploading} className="w-full bg-[#0073ea] hover:bg-[#0060c2] text-white">
                {uploading ? <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Upload Template'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Templates Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48 bg-gray-200 rounded-xl" />)}
        </div>
      ) : templates.length === 0 ? (
        <Card className="bg-white border-gray-200 border-dashed shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <FileSignature className="h-12 w-12 text-gray-300 mb-4" strokeWidth={1.5} />
            <p className="text-gray-500 text-lg font-medium">No templates yet</p>
            <p className="text-gray-400 text-sm mt-1">Upload your first contract template to get started</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map(tmpl => (
            <EditableTemplateCard
              key={tmpl.id}
              tmpl={tmpl}
              onUpdate={fetchTemplates}
              onDelete={handleDelete}
              onDownload={handleDownload}
              onEditFields={setEditorTemplateId}
            />
          ))}
        </div>
      )}
    </div>
  );
}
